import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import {
	Box,
	Button,
	Typography,
	Grid2 as Grid,
	Divider,
	CircularProgress,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { gql, useLazyQuery } from "@apollo/client";
import DownloadIcon from "../../assets/pngs/downloadIcon.png";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { NotificationToaster } from "../../utils/NotificationToaster";
import { useNavigate } from "react-router-dom";

const StyledBackButton = styled(Button)(() => ({
	height: "34px",
	width: "110px",
	backgroundColor: "#025131",
	fontFamily: "Inter",
	fontWeight: "bold",
	fontSize: "20px",
	color: "#fff",
	letterSpacing: 0,
	border: "1px solid #025131",
	borderRadius: "4px",
	"&:hover": {
		backgroundColor: "#025131",
		color: "#fff",
	},
	textTransform: "none",
}));

const StyledBorderBox = styled(Box)(() => ({
	border: "1px solid #D9D9D9",
	padding: "0.2rem 1rem",
}));

const DocumentWizard = ({ docType: propDocType, uuid: propUuid }) => {
	// Extract uuid from URL params if not provided as prop
	const { uuid: urlUuid } = useParams();

	// Use prop values if available, otherwise use URL params
	const uuid = propUuid || urlUuid;
	// If docType is not provided, use uuid as docType (they are the same in many cases)
	const docType = propDocType || uuid;
	const ideaId = useSelector((state) => state?.explore?.idea?.id);
	const navigate = useNavigate();

	const [docData, setDocData] = useState(null);
	const [iframeURL, setIframeURL] = useState(null);
	const [iframeLoader, setIframeLoader] = useState(false);

	const handleGetDocIframe = async (data) => {
		let payload = JSON.stringify({
			docno: data?.idea?.regulations[0]?.id || "",
			rev: data?.idea?.regulations?.revisionNumber || "",
			title: data?.idea?.regulations[0]?.id || "",
			dateOfAssessment: data?.today || "",
			deviceName: data?.idea?.deviceName || "",
			modalNumber: data?.idea?.productModel || "",
			deviceIntendedUse: data?.idea?.intendedUse || "",
			indicationsForUse: data?.idea?.indicationsForUse || "",
			deviceDescription: data?.idea?.summary || "",
			device: data?.idea?.deviceName || "",
			regularDescription:
				data?.idea?.regulations[0]?.excerpt?.substring(0, 100) || "",
			productCode: data?.idea?.productCode?.code || "",
			submissionType: data?.idea?.productCode?.submissionType || "",
			regulationNumber: data?.idea?.regulations[0]?.id || "",
			deviceClass: data?.idea?.productCode?.deviceClassCode || "",
			identification:
				data?.idea?.regulations[0]?.identification?.substring(0, 100) || "",
			exemptRationale: data?.idea?.regulations[0]?.excerpt || "",
			email: "rhuang@havideaconsulting.com",
		});

		try {
			const res = await fetch(
				"https://accelidea-be-staging-gzfahpbyehbrbyhx.eastus-01.azurewebsites.net/docusign/embedded-url",
				{
					method: "POST",
					headers: {
						"Content-Type": "application/json",
					},
					body: payload,
				}
			);

			const data = await res.json();
			setIframeURL(data?.url);
			setIframeLoader(false);
		} catch (error) {
			console.error("Error:", error);
			NotificationToaster({
				message: "Docusign document could not be generated",
				type: "error",
			});
			setIframeLoader(false);
		}
	};

	const GET_DOCUMENT_DATA_GQL = gql`
		query GetDocumentData($id: ID!) {
			today # Date of Assessment / Date in Approval Signatures
			idea(id: $id) {
				deviceName # Device Name / Device
				productModel # Model Number
				revisionNumber # Revision Number
				intendedUse # Device Intended Use
				indicationsForUse # Indications for Use
				summary # Device Description
				intendedContact # Choose intended contact of the particular material
				typeOfTissueContact # Choose the type of tissue contact of your tissue contacting material
				durationOfContact # Duration of Contact
				regulations {
					title # Regulation Title
					id # Regulation Number
					excerpt # Regulation Description
					identification # Regulation Identification
				}
				productCode {
					code # Product Code
					deviceClassCode # Device Class
					submissionType # Submission Type
				}
				standards {
					developingOrganization # Standard Organization
					designationNumberAndDate # Designation Number and Edition/Date
					title # Standard Title
				}
				guidances {
					title # Guidance Title
				}
				products {
					id
					name
					status
					productCode {
						code
					}
				}
			}
			me {
				name # Assessment by / Head of Regulatory Affairs or Designee
			}
		}
	`;

	const [getDocumentInformation, { loading: GetDocumentDataLoading }] =
		useLazyQuery(GET_DOCUMENT_DATA_GQL, {
			fetchPolicy: "no-cache",
		});

	const handleGetRegDocumentInfo = () => {
		getDocumentInformation({
			variables: {
				id: ideaId,
			},
		})
			.then((res) => {
				setDocData(res?.data);
				setIframeLoader(true);
				handleGetDocIframe(res?.data);
			})
			.catch((err) => {
				console.log(err);
				NotificationToaster({
					message: "Document Information could not be fetched",
					type: "error",
				});
			});
	};

	const handleODocDocIframe = async (data) => {
		let payload = {
			templateId: uuid,
			email: "abdul.azeem@rocketfarm.com",
			fields: {
				product_name: data?.idea?.deviceName || "",
			},
		};

		if (data?.idea?.standards?.length === 0) {
			payload["fields"][`stand_org_1`] = "";
			payload["fields"][`design_num_1`] = "";
			payload["fields"][`stand_title_1`] = "";
			payload["fields"][`asca_conf_1`] = "";
			payload["fields"][`geo_1`] = "";
		}

		if (data?.idea?.regulations?.length === 0) {
			payload["fields"][`reg_num_1`] = "";
			payload["fields"][`reg_title_1`] = "";
			payload["fields"][`reg_status_1`] = "";
			payload["fields"][`reg_date_1`] = "";
		}

		if (data?.idea?.guidances?.length === 0) {
			payload["fields"][`guide_dock_num_1`] = "";
			payload["fields"][`guide_title_1`] = "";
			payload["fields"][`guide_status_1`] = "";
			payload["fields"][`guide_date_1`] = "";
		}

		data?.idea?.standards?.slice(0, 4)?.forEach((doc, index) => {
			const docNumber = index + 1;

			payload["fields"][`stand_org_${docNumber}`] =
				doc?.developingOrganization || "";
			payload["fields"][`design_num_${docNumber}`] =
				doc?.designationNumberAndDate || "";
			payload["fields"][`stand_title_${docNumber}`] = doc?.title || "";
			payload["fields"][`asca_conf_${docNumber}`] =
				doc?.developingOrganization || "No";
			payload["fields"][`geo_${docNumber}`] = doc?.geo || "";
		});

		data?.idea?.regulations?.slice(0, 4)?.forEach((reg, index) => {
			const regNumber = index + 1;

			payload["fields"][`reg_num_${regNumber}`] = reg?.id || "";
			payload["fields"][`reg_title_${regNumber}`] = reg?.title || "";
			payload["fields"][`reg_status_${regNumber}`] = reg?.regStatus || "";
			payload["fields"][`reg_date_${regNumber}`] = reg?.regDate || "";
		});

		data?.idea?.guidances?.slice(0, 4)?.forEach((guide, index) => {
			const guideNumber = index + 1;

			payload["fields"][`guide_dock_num_${guideNumber}`] =
				guide?.guideDocNumber || "";
			payload["fields"][`guide_title_${guideNumber}`] = guide?.title || "";
			payload["fields"][`guide_status_${guideNumber}`] =
				guide?.guideStatus || "";
			payload["fields"][`guide_date_${guideNumber}`] = guide?.guideDate || "";
		});

		try {
			const res = await fetch(
				"https://accelidea-be-staging-gzfahpbyehbrbyhx.eastus-01.azurewebsites.net/docusign/new-envelope",
				{
					method: "POST",
					headers: {
						"Content-Type": "application/json",
					},
					body: JSON.stringify(payload),
				}
			);

			const data = await res.json();
			setIframeURL(data?.url);
			setIframeLoader(false);
		} catch (error) {
			console.error("Error:", error);
			NotificationToaster({
				message: "Docusign document could not be generated",
				type: "error",
			});
			setIframeLoader(false);
		}
	};

	const GET_ODOC_DOCUMENT_DATA_GQL = gql`
		query GetDocumentData($id: ID!) {
			idea(id: $id) {
				deviceName # Device Name
				standards {
					developingOrganization # Standard Organization
					designationNumberAndDate # Designation Number and Edition/Date
					title # Standard Title
				}
				regulations {
					id # Regulation Number
					title # Regulation Title
				}
				guidances {
					title # Guidance Title
				}
			}
			me {
				name # Head of Quality or Designee / Head of Regulatory Affairs or Designee / Head of R&D
			}
			today # Date
		}
	`;

	const [getODocDocumentInformation, { loading: GetODocDocumentDataLoading }] =
		useLazyQuery(GET_ODOC_DOCUMENT_DATA_GQL, {
			fetchPolicy: "no-cache",
		});

	const handleGetODocDocumentInfo = () => {
		getODocDocumentInformation({
			variables: {
				id: ideaId,
			},
		})
			.then((res) => {
				setDocData(res?.data);
				setIframeLoader(true);
				handleODocDocIframe(res?.data);
			})
			.catch((err) => {
				console.log(err);
				NotificationToaster({
					message: "Document Information could not be fetched",
					type: "error",
				});
			});
	};

	const GenerateDocusignAPIPayload = (data) => {
		let payload = {
			templateId: docType,
			email: "abdul.azeem@rocketfarm.com",
			fields: {},
		};

		if (docType === "8a5114c7-dd8f-4351-a234-ed9cd5e7c80a") {
			if (data?.idea?.standards?.length === 0) {
				payload["fields"][`Organization_1`] = "";
				payload["fields"][`Designation_1`] = "";
				payload["fields"][`StandardTitle_1`] = "";
				payload["fields"][`RecognitionId_1`] = "";
			} else {
				data?.idea?.standards?.slice(0, 4)?.forEach((doc, index) => {
					const docNumber = index + 1;

					payload["fields"][`Organization_${docNumber}`] =
						doc?.developingOrganization || "";
					payload["fields"][`Designation_${docNumber}`] =
						doc?.designationNumberAndDate || "";
					payload["fields"][`StandardTitle_${docNumber}`] = doc?.title || "";
					payload["fields"][`RecognitionId_${docNumber}`] =
						doc?.developingOrganization || "";
				});
			}
		} else if (docType === "b182abdd-e4b5-4618-b320-384f4c28d01b") {
			if (data?.idea?.regulations?.length === 0) {
				payload["fields"][`Regulations_1`] = "";
			} else {
				data?.idea?.regulations?.slice(0, 3)?.forEach((reg, index) => {
					const docNumber = index + 1;

					payload["fields"][`Regulations_${docNumber}`] = reg?.id || "";
				});
			}

			const confirmedProducts = data?.idea?.products?.filter(
				product =>
					product.status === "CONFIRMED"
			) || [];

			if (confirmedProducts.length === 0) {
				payload["fields"][`Exempt_1`] = "";
				payload["fields"][`ProductName_1`] = "";
				payload["fields"][`ProductId_1`] = "";
				payload["fields"][`ProductCode_1`] = "";
			} else {
				payload["fields"][`Exempt_1`] = "No";
				payload["fields"][`ProductName_1`] = confirmedProducts[0]?.name || "";
				payload["fields"][`ProductId_1`] = confirmedProducts[0]?.id || "";
				payload["fields"][`ProductCode_1`] =
					confirmedProducts[0]?.productCode?.code || "";
			}

			payload["fields"]["MedicalSpeciality_1"] = "";
		} else if (docType === "fba0c791-7dff-4440-8199-5d4ea2baab6c") {
			if (data?.idea?.guidances?.length === 0) {
				payload["fields"][`Guidance`] = "";
			} else {
				payload["fields"][`Guidance`] =
					data?.idea?.guidances[0]?.title?.slice(0, 80) || "";
			}
		} else if (docType === "7cbedf38-6532-44bd-99fa-6f44bb4cfe2f") {
			payload["fields"]["IdentifyItems"] = data?.idea?.identifyItems || "";
			payload["fields"]["MaterialUsed"] = data?.idea?.materialsUsed || "";
			payload["fields"]["IntendedContact"] = data?.idea?.intendedContact || "";
			payload["fields"]["FdaSubmissionNum"] =
				data?.idea?.fdaSubmissionNum || "";
			payload["fields"]["RepeatPotential"] = data?.idea?.repeatPotentials || "";
			payload["fields"]["TissueType"] = data?.idea?.typeOfTissueContact || "";
			payload["fields"]["ContactDuration"] =
				data?.idea?.durationOfContact || "";
		}

		return payload;
	};

	const handleOtherDocumentInfoIframe = async (data) => {
		const payload = GenerateDocusignAPIPayload(data);

		try {
			const res = await fetch(
				"https://accelidea-be-staging-gzfahpbyehbrbyhx.eastus-01.azurewebsites.net/docusign/new-envelope-template",
				{
					method: "POST",
					headers: {
						"Content-Type": "application/json",
					},
					body: JSON.stringify(payload),
				}
			);

			const data = await res.json();
			setIframeURL(data?.url);
			setIframeLoader(false);
		} catch (error) {
			console.error("Error:", error);
			NotificationToaster({
				message: "Docusign document could not be generated",
				type: "error",
			});
			setIframeLoader(false);
		}
	};

	const [getOtherDocumentInfo, { loading: GetOtherDocumentInfoLoading }] =
		useLazyQuery(GET_DOCUMENT_DATA_GQL, {
			fetchPolicy: "no-cache",
		});

	const handleOtherDocDocumentInfo = () => {
		getOtherDocumentInfo({
			variables: {
				id: ideaId,
			},
		})
			.then((res) => {
				console.log(res);
				setDocData(res?.data);
				setIframeLoader(true);
				handleOtherDocumentInfoIframe(res?.data);
			})
			.catch((err) => {
				console.log(err);
				NotificationToaster({
					message: "Document Information could not be fetched",
					type: "error",
				});
			});
	};

	const handleNoParamDoc = async () => {
		const payload = {
			templateId: docType,
			email: "abdul.azeem@rocketfarm.com",
			fields: {},
		};

		try {
			const res = await fetch(
				"https://accelidea-be-staging-gzfahpbyehbrbyhx.eastus-01.azurewebsites.net/docusign/new-envelope-template",
				{
					method: "POST",
					headers: {
						"Content-Type": "application/json",
					},
					body: JSON.stringify(payload),
				}
			);

			const data = await res.json();
			setIframeURL(data?.url);
			setIframeLoader(false);
		} catch (error) {
			console.error("Error:", error);
			NotificationToaster({
				message: "Docusign document could not be generated",
				type: "error",
			});
			setIframeLoader(false);
		}
	};

	useEffect(() => {
		if (!ideaId) return;

		if (docType === "c237858e-6493-495e-8486-edc5dff30717") {
			handleGetRegDocumentInfo();
		} else if (docType === "d43cfa67-2969-42ed-9005-2af044899e3f") {
			handleGetODocDocumentInfo();
		} else if (
			docType === "f1ec0413-1846-4c9e-ae9c-ffaf47b204e5" ||
			docType === "c07eb12e-a289-44d5-bec4-be496c04213e" ||
			docType === "17be62b9-882f-4baa-8d93-cd195ffdbcfc"
		) {
			handleNoParamDoc();
		} else {
			handleOtherDocDocumentInfo();
		}
	}, [ideaId, docType, uuid]);

	return (
		<>
			<Box
				style={{
					display: "flex",
				}}
			>
				<StyledBackButton onClick={() => navigate('/Documents')}>
					<ArrowBackIcon
						style={{
							marginRight: 5,
						}}
					/>
					Back
				</StyledBackButton>
			</Box>
			<Box
				sx={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
				}}
			>
				<Typography
					fontWeight={"bold"}
					fontFamily={"Inter"}
					fontSize={"20px"}
					color={"#038C5A"}
					display="inline"
				>
					Document Wizard
				</Typography>

				<img src={DownloadIcon} alt="" width={"39px"} height={"39px"} />
			</Box>
			{GetDocumentDataLoading ||
			GetODocDocumentDataLoading ||
			GetOtherDocumentInfoLoading ? (
				<Box
					style={{
						display: "flex",
						width: "100%",
						height: "50%",
						justifyContent: "center",
						alignItems: "center",
					}}
				>
					<CircularProgress />
				</Box>
			) : (
				<Grid container spacing={4}>
					<Grid
						item
						size={{
							xs: 2.5,
						}}
					>
						<StyledBorderBox
							style={{
								marginBottom: "1rem",
							}}
						>
							<Typography
								fontFamily={"Inter"}
								fontWeight={700}
								fontSize={"20px"}
								color={"#303030"}
								textAlign={"center"}
							>
								Document Information
							</Typography>
						</StyledBorderBox>

						<StyledBorderBox>
							<Box
								style={{
									display: "flex",
									flexDirection: "column",
									minHeight: "68.5vh",
								}}
							>
								<Typography
									fontFamily={"Inter"}
									fontWeight={700}
									fontSize={"20px"}
									color={"#303030"}
									textAlign={"center"}
									mb={"1rem"}
								>
									In This Document
								</Typography>

								{/* <Typography
                    fontFamily={"Inter"}
                    fontWeight={700}
                    fontSize={"16px"}
                    color={"#303030"}
                    sx={{
                        textDecoration: "underline",
                        fontStyle: "italic",
                    }}
                    mb={"0.5rem"}
                >
                    Latest Changes:
                </Typography> */}

								{/* <ul style={{ paddingLeft: "20px", marginTop: "8px" }}>
                    <li>
                        <Typography
                            fontFamily={"Inter"}
                            fontWeight={400}
                            fontSize={"16px"}
                            lineHeight={"19px"}
                            color={"#303030"}
                        >
                            <strong>ISO 10993-5 & ISO 10993-10</strong> were added to
                            List of Applicable Standards document based on{" "}
                            <em>Decision Tree by User-123</em>
                        </Typography>
                    </li>
                </ul> */}

								<Typography
									fontFamily={"Inter"}
									fontWeight={700}
									fontSize={"16px"}
									color={"#303030"}
									mb={"2rem"}
								>
									Device Name: {docData?.idea?.deviceName}
								</Typography>

								<Typography
									fontFamily={"Inter"}
									fontWeight={700}
									fontSize={"20px"}
									color={"#303030"}
									lineHeight={"24px"}
									textAlign={"center"}
									mb={"1rem"}
								>
									Found In Other Documents
								</Typography>

								<Typography
									fontFamily={"Inter"}
									fontWeight={700}
									fontSize={"16px"}
									color={"#303030"}
									mb={"0.25rem"}
								>
									Document 1
								</Typography>

								<Divider
									style={{
										marginTop: "2rem",
										minWidth: "70%",
										backgroundColor: "#626262",
									}}
								/>

								<Typography
									fontFamily={"Inter"}
									fontWeight={700}
									fontSize={"20px"}
									color={"#303030"}
									lineHeight={"20px"}
									textAlign={"center"}
									mb={"2rem"}
									mt={"1rem"}
								>
									Document Required By
								</Typography>

								<Typography
									fontFamily={"Inter"}
									fontWeight={700}
									fontSize={"16px"}
									lineHeight={"19px"}
									color={"#303030"}
									textAlign={"center"}
									mb={"1rem"}
									sx={{
										fontStyle: "italic",
									}}
								>
									{/* FDA - 21 CFR Part 820.30 */}
									{docData?.idea?.regulations[0]?.id}
								</Typography>
							</Box>
						</StyledBorderBox>
					</Grid>

					<Grid
						item
						size={{
							xs: 9.5,
						}}
					>
						<StyledBorderBox
							style={{
								width: "100%",
								height: "75vh",
								padding: 0,
							}}
						>
							{!iframeLoader ? (
								<iframe
									src={iframeURL}
									title="Document Wizard"
									height="100%"
									width="100%"
									style={{ border: "none" }}
								/>
							) : (
								<Box
									style={{
										display: "flex",
										width: "100%",
										height: "100%",
										justifyContent: "center",
										alignItems: "center",
									}}
								>
									<CircularProgress />
								</Box>
							)}
						</StyledBorderBox>
					</Grid>
				</Grid>
			)}{" "}
		</>
	);
};

export default DocumentWizard;
